import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSuggestionsButtonAction } from '../../hooks/use-suggestions-button-action';
import { SuggestionsDefaultListPinnedItems } from '../suggestions-list/suggestions-default-list-pinned-items';
import { useSponsoredProducts } from 'components/header/hooks';
import { CategoriesList } from '../categories-list';
import { BrandsList } from '../brands-list';
import { useSponsoredBrands } from '../../hooks';
import { Akinator, CompletionsAkinator } from '../akinator';
import { getUpdatedQueryByCompletionType } from '../utils/get-update-query-by-completion-type';
import { getDefaultListPinnedOffset } from '../utils/get-default-list-navigable-pinned-items';
import { NewSuggestionsContent } from './new-suggestions-content';
import { AkinatorCompletion, SlicedSuggestions } from 'types/suggestions';
import { PinnedSuggestion } from 'types/model';
import { LangType } from 'common/constants';
import { SearchSuggestionsListbox } from '../search-suggestions-listbox';
import { shouldSplitNewSuggestionsAroundSponsored } from '../utils/split-new-suggestions-for-sponsored';

type SuggestionsProps = {
  searchQuery: string;
  updateSearchQuery: (value: string) => void;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  onSelectSuggestion?: () => void;
  searchHref: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  pinnedSuggestions: PinnedSuggestion[];
  completions: AkinatorCompletion[] | null;
  showAkinatorCompletions: boolean;
  isKzDomain: boolean;
  lang: LangType;
};

export const Suggestions = memo<SuggestionsProps>(
  ({
    searchQuery,
    updateSearchQuery,
    suggestions,
    suggestionSelector,
    onMouseEnter,
    onMouseLeave,
    searchHref,
    index,
    isSrp,
    onSelectSuggestion,
    pinnedSuggestions,
    completions,
    showAkinatorCompletions,
    isKzDomain,
    lang,
  }) => {
    const [newSuggestions, slicedCategories] = suggestions ?? [[], []];
    const sponsoredProducts = useSponsoredProducts();
    const hasSponsoredProducts = sponsoredProducts.length > 0;
    const { itemsToDisplay: sponsoredBrands } = useSponsoredBrands();
    const showProducts = !(isKzDomain && lang === 'kk');
    const isDefaultList = searchQuery === '';
    const showOnlyCategories = isDefaultList && slicedCategories.length > 0;
    const akinatorOffset = showAkinatorCompletions ? (completions?.length ?? 0) : 0;
    const defaultListPinnedOffset =
      isDefaultList && !isKzDomain ? getDefaultListPinnedOffset(pinnedSuggestions.length) : 0;

    const showBrands = sponsoredBrands.length > 0;
    const shouldSplitAroundSponsored = shouldSplitNewSuggestionsAroundSponsored(
      hasSponsoredProducts,
      showOnlyCategories,
    );

    const buttonAction = useSuggestionsButtonAction({
      onMouseEnter,
      onMouseLeave,
      onSelectSuggestion,
      searchQuery,
      isPersonalization: showOnlyCategories,
    });

    const { t } = useTranslation();

    if (!suggestions) return null;

    return (
      <>
        {isDefaultList && !isKzDomain ? <Akinator /> : null}

        <SearchSuggestionsListbox aria-label={t('header.searchbar.search')}>
          {isDefaultList && !isKzDomain ? (
            <SuggestionsDefaultListPinnedItems
              buttonAction={buttonAction}
              index={index}
              isSrp={isSrp}
              suggestionSelector={suggestionSelector}
              pinnedSuggestions={pinnedSuggestions}
            />
          ) : null}

          {showAkinatorCompletions ? (
            <CompletionsAkinator
              completions={completions ?? []}
              navigationIndex={index}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onCompletionClick={(completion) => {
                updateSearchQuery(getUpdatedQueryByCompletionType(searchQuery, completion));
              }}
            />
          ) : null}

          <NewSuggestionsContent
            newSuggestions={newSuggestions}
            hasSponsoredProducts={hasSponsoredProducts}
            showOnlyCategories={showOnlyCategories}
            showProducts={showProducts}
            suggestionSelector={suggestionSelector}
            isDefaultList={isDefaultList}
            searchHref={searchHref}
            index={index}
            isSrp={isSrp}
            isKzDomain={isKzDomain}
            akinatorOffset={akinatorOffset}
            defaultListPinnedOffset={defaultListPinnedOffset}
            searchQuery={searchQuery}
            suggestions={suggestions}
            sponsoredProducts={sponsoredProducts}
            buttonAction={buttonAction}
          />

          <CategoriesList
            suggestionSelector={suggestionSelector}
            isDefaultList={isDefaultList}
            slicedCategories={slicedCategories}
            productsLength={newSuggestions.length}
            suggestions={suggestions}
            index={index}
            isSrp={isSrp}
            buttonAction={buttonAction}
            showOnlyCategories={showOnlyCategories}
            akinatorOffset={akinatorOffset}
            defaultListPinnedOffset={defaultListPinnedOffset}
          />
        </SearchSuggestionsListbox>

        {shouldSplitAroundSponsored && showBrands ? (
          <BrandsList suggestionSelector={suggestionSelector} searchQuery={searchQuery} />
        ) : null}
      </>
    );
  },
);
